/**
 * Lead Tracking Utility
 * Tracks first-time visitors and user interactions for lead generation
 */

import logger from './logger';

const LEAD_TRACKING_KEY = 'coursolo_lead_tracking';
const FIRST_VISIT_KEY = 'coursolo_first_visit';
const VISIT_COUNT_KEY = 'coursolo_visit_count';

/**
 * Generate or retrieve visitor ID
 */
const getVisitorId = () => {
    let visitorId = localStorage.getItem('coursolo_visitor_id');
    if (!visitorId) {
        visitorId = `visitor_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
        localStorage.setItem('coursolo_visitor_id', visitorId);
    }
    return visitorId;
};

/**
 * Check if this is the first visit
 */
export const isFirstVisit = () => {
    const firstVisit = sessionStorage.getItem(FIRST_VISIT_KEY);
    if (!firstVisit) {
        sessionStorage.setItem(FIRST_VISIT_KEY, 'true');
        return true;
    }
    return false;
};

/**
 * Get visit count
 */
export const getVisitCount = () => {
    const count = parseInt(localStorage.getItem(VISIT_COUNT_KEY) || '0', 10);
    return count;
};

/**
 * Increment visit count
 */
const incrementVisitCount = () => {
    const count = getVisitCount() + 1;
    localStorage.setItem(VISIT_COUNT_KEY, count.toString());
    return count;
};

/**
 * Track page view
 */
export const trackPageView = async (pageName, additionalData = {}) => {
    const visitorId = getVisitorId();
    const isFirst = isFirstVisit();
    const visitCount = incrementVisitCount();
    
    const trackingData = {
        visitorId,
        page: pageName,
        isFirstVisit: isFirst,
        visitCount,
        timestamp: new Date().toISOString(),
        url: window.location.pathname,
        referrer: document.referrer || 'direct',
        userAgent: navigator.userAgent,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        ...additionalData
    };

    // Store locally for batch sending
    const events = JSON.parse(localStorage.getItem(LEAD_TRACKING_KEY) || '[]');
    events.push({
        type: 'page_view',
        ...trackingData
    });
    localStorage.setItem(LEAD_TRACKING_KEY, JSON.stringify(events));

    // Send to server if first visit or important page
    if (isFirst || ['home', 'landing', 'pricing', 'signup'].includes(pageName)) {
        await sendTrackingData(trackingData);
    }

    return trackingData;
};

/**
 * Track user interaction
 */
export const trackInteraction = async (eventType, eventData = {}) => {
    const visitorId = getVisitorId();
    const visitCount = getVisitCount();
    
    const trackingData = {
        visitorId,
        eventType,
        visitCount,
        timestamp: new Date().toISOString(),
        url: window.location.pathname,
        ...eventData
    };

    // Store locally
    const events = JSON.parse(localStorage.getItem(LEAD_TRACKING_KEY) || '[]');
    events.push({
        type: 'interaction',
        ...trackingData
    });
    localStorage.setItem(LEAD_TRACKING_KEY, JSON.stringify(events));

    // Send important interactions immediately
    const importantEvents = ['signup_click', 'pricing_click', 'create_course_click', 'cta_click'];
    if (importantEvents.includes(eventType)) {
        await sendTrackingData(trackingData);
    }

    return trackingData;
};

/**
 * Send tracking data to server
 */
const sendTrackingData = async (data) => {
    try {
        const { serverURL } = await import('../constants');
        const { authedFetch } = await import('../shared/auth/authedFetch');
        await authedFetch(`${serverURL}/api/analytics/track`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
            keepalive: true // Ensure request completes even if page unloads
        });
    } catch (error) {
        logger.warn('Failed to send tracking data:', error);
        // Data is stored locally, will be sent later
    }
};

/**
 * Flush all pending tracking events
 */
export const flushTrackingEvents = async () => {
    const events = JSON.parse(localStorage.getItem(LEAD_TRACKING_KEY) || '[]');
    if (events.length === 0) return;

    try {
        const { serverURL } = await import('../constants');
        const { authedFetch } = await import('../shared/auth/authedFetch');
        await authedFetch(`${serverURL}/api/analytics/track-batch`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ events }),
            keepalive: true
        });

        // Clear sent events
        localStorage.removeItem(LEAD_TRACKING_KEY);
    } catch (error) {
        logger.warn('Failed to flush tracking events:', error);
    }
};

/**
 * Initialize lead tracking
 */
export const initLeadTracking = () => {
    // Track initial page view
    const currentPage = window.location.pathname.replace('/', '') || 'home';
    trackPageView(currentPage);

    // Set up periodic flushing
    setInterval(() => {
        flushTrackingEvents();
    }, 60000); // Flush every minute

    // Flush on page unload
    window.addEventListener('beforeunload', () => {
        flushTrackingEvents();
    });

    // Track visibility changes
    document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
            flushTrackingEvents();
        }
    });
};

/**
 * Track CTA clicks
 */
export const trackCTAClick = (ctaName, location) => {
    trackInteraction('cta_click', {
        ctaName,
        location,
        element: 'button'
    });
};

/**
 * Track feature exploration
 */
export const trackFeatureView = (featureName) => {
    trackInteraction('feature_view', {
        featureName,
        element: 'feature_card'
    });
};

